import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Unlock } from "lucide-react";

/**
 * Site Admin → Subscriptions → Manage Tier modal (iter42, extracted iter43d).
 *
 * Lets the site admin grant/revoke `comp` (complimentary) tier on a league.
 *
 * iter87.92 — Retired the "Paid — manual override" option. Under the unified
 * Pro+ model, `paid` at the league level is derived from the OWNER's
 * user-level Pro+ subscription — grant Pro+ to the owner via Pro+
 * Subscriptions and every rivalry they own automatically inherits Pro+.
 * The league-level `paid` writer at the API was already retired in
 * iter87.73; this modal just catches up to that reality.
 */
export default function ManageTierModal({ league, onClose, onSave, saving }) {
    const sub = league.subscription;
    const [tier, setTier] = useState(sub.stored_tier || "free");
    const [compReason, setCompReason] = useState(sub.comp_reason || "");
    const [compExpiresAt, setCompExpiresAt] = useState(
        sub.comp_expires_at ? sub.comp_expires_at.split("T")[0] : ""
    );

    const handleSave = () => {
        const payload = { tier };
        if (tier === "comp") {
            if (!compReason.trim()) {
                toast.error("Reason is required for comp tier");
                return;
            }
            payload.comp_reason = compReason.trim();
            if (compExpiresAt) {
                // Convert date input to end-of-day UTC ISO
                payload.comp_expires_at = new Date(compExpiresAt + "T23:59:59Z").toISOString();
            } else {
                payload.comp_expires_at = null;
            }
        }
        onSave(payload);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            data-testid="manage-tier-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div className="brutal-card p-6 max-w-md w-full bg-ink border-black space-y-4">
                <div className="flex items-start gap-3">
                    <CreditCard className="w-6 h-6 text-volt shrink-0 mt-1" />
                    <div>
                        <h3 className="font-display font-black text-xl uppercase">Manage Subscription</h3>
                        <p className="text-xs text-zinc-400 mt-1">{league.name}</p>
                        <p className="text-[10px] text-zinc-600 font-mono mt-0.5">{league.id}</p>
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Tier</label>
                    <select
                        data-testid="modal-tier-select"
                        value={tier}
                        onChange={(e) => setTier(e.target.value)}
                        className="brutal-input w-full"
                        disabled={saving}
                    >
                        <option value="free">Free — default, 1 active tournament</option>
                        <option value="comp">Comp — site-admin-granted, no billing</option>
                    </select>
                    <p className="text-[10px] text-zinc-500 mt-1">
                        Need to grant Pro+? Pro+ is a{" "}
                        <span className="text-cyber font-bold">user-level</span>{" "}
                        entitlement — grant it to the rivalry's owner in{" "}
                        <span className="text-cyber">Pro+ Subscriptions</span>{" "}
                        and every rivalry they own inherits Pro+ automatically.
                    </p>
                </div>

                {tier === "comp" && (
                    <>
                        <div data-testid="modal-comp-reason-wrapper">
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                                Reason <span className="text-blaze">*required</span>
                            </label>
                            <input
                                data-testid="modal-comp-reason-input"
                                type="text"
                                placeholder="beta_tester, partnership, promo:CODE…"
                                value={compReason}
                                onChange={(e) => setCompReason(e.target.value)}
                                className="brutal-input w-full"
                                disabled={saving}
                            />
                            <p className="text-[10px] text-zinc-500 mt-1">Free-text. Used for tracking GTM, partnerships, beta users.</p>
                        </div>
                        <div data-testid="modal-comp-expires-wrapper">
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                                Expiry date <span className="text-zinc-600">(optional — leave blank for indefinite)</span>
                            </label>
                            <input
                                data-testid="modal-comp-expires-input"
                                type="date"
                                value={compExpiresAt}
                                onChange={(e) => setCompExpiresAt(e.target.value)}
                                className="brutal-input w-full"
                                disabled={saving}
                                min={new Date().toISOString().split("T")[0]}
                            />
                            <p className="text-[10px] text-zinc-500 mt-1">When the comp sunsets, the rivalry auto-downgrades to Free.</p>
                        </div>
                    </>
                )}

                {tier === "free" && sub.stored_tier !== "free" && (
                    <div className="brutal-card-light p-3 flex items-start gap-2 border-zinc-600" data-testid="modal-free-warning">
                        <Unlock className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-zinc-300 leading-relaxed">
                            Switching to <span className="text-zinc-200 font-bold">Free</span> will clear all comp / paid metadata for this rivalry. Multi-tournament capability will be revoked when that feature ships.
                        </p>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        data-testid="modal-cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                        className="brutal-btn-ghost text-xs"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="modal-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                        className="brutal-btn text-xs bg-volt text-black border-black shadow-brutal disabled:opacity-40"
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
